import { HandledError, isHandledErrorJson } from "@common/consts/errors";
import type { APIResponse } from "@common/utils/api-utils";

export type Http = ReturnType<typeof makeHttp>;

export function makeHttp(
  domain = "api/v1",
  otherHeaders: Record<string, string> = {}
) {
  let accessToken: string | null = null;

  return {
    json,
    getAccessToken,
    setAccessToken,
  };

  async function json<T extends Record<string, any>, K>(
    method: "GET" | "PUT" | "POST" | "DELETE",
    url: string,
    params: T | null = null,
    {
      mode = "same-origin",
      headers = {},
    }: { cache?: boolean; mode?: string; headers?: RequestInit["headers"] } = {}
  ): Promise<K> {
    // if (!accessToken) {
    //   throw new Error("Access token missing, please login");
    // }

    let data: RequestInit = {
      method: method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: accessToken || "",
        ...headers,
        ...otherHeaders,
      },
      keepalive: ["PUT", "POST", "DELETE"].includes(method), // make sure updates get through
    };

    if (mode == "same-origin") {
      data.credentials = mode;
      data.mode = mode;
    }

    let finalUrl = url;
    if (method === "GET" && params) {
      const paramStr = new URLSearchParams(params).toString();
      finalUrl += `?${paramStr}`;
    } else if (params) {
      data.body = JSON.stringify(params);
    }

    try {
      let response = await fetch(`${domain}${finalUrl}`, data);

      if (response.headers.get("content-length") == "0") {
        throw {
          status: "EMPTY_RESPONSE",
        };
      }

      let contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        let jsonResponse = (await response.json()) as APIResponse<K>;

        if (!jsonResponse.error) return jsonResponse.data;

        if (isHandledErrorJson(jsonResponse.error))
          throw HandledError.fromHandledErrorJson(jsonResponse.error);

        throw new Error(jsonResponse.error.message);
      }

      return response as unknown as K;
    } catch (error: any) {
      if (error instanceof HandledError) throw error;
      if (error.code && error.status && error.message) {
        throw error;
      }

      if (error.message == "Network request failed") {
        throw {
          // code: error.
          status: "NO_INTERNET",
          statusText: "Connection Failed",
          message: "Please check your Internet connection",
          data: { url: finalUrl },
        };
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  function setAccessToken(token: string | null) {
    accessToken = token;
  }

  function getAccessToken() {
    return accessToken;
  }
}
